import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, X, FileText, BadgeCheck, Minus, Printer, RefreshCw, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, API, eur, formatApiError, ORDER_STATUS, CATEGORY_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminMessenger } from "@/components/Messaging";
import { AdminNotifications } from "@/components/Notifications";
import { AdminUsers } from "@/components/Users";
import { AdminClients } from "@/components/Clients";
import { AdminProducers } from "@/components/Producers";
import { AdminQrCodes } from "@/components/QrCodes";
import AdminReservations from "@/components/Reservations";
import { pressZoom } from "@/lib/usePressZoom";
import ImageZoom from "@/components/ImageZoom";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-2.5 text-base outline-none focus:border-[#6B1226]/50 transition-colors";

const EMPTY_WINE = {
  name: "", category: "rouge", producer: "", appellation: "", region: "", year: "", price: "", stock: 0, description: "", image: "", featured: false,
  volume: "", alcohol_degree: "", cepages: "", garde: "", temperature_service: "", allergenes: "",
  supplier_name: "", supplier_contact: "", supplier_phone: "", supplier_email: "", supplier_website: "", supplier_address: "",
};

const PAYMENT_METHODS = ["Carte bancaire au restaurant", "Espèces", "Chèque"];

const uniqueDocs = (docs = []) => [...new Map(docs.map((d) => [d.type, d])).values()];

// Imprime un document PDF directement (récupéré avec la session, envoyé à l'imprimante)
export async function printDocument(orderId, docType) {
  const res = await api.get(`/orders/${orderId}/documents/${docType}`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  };
  setTimeout(() => {
    URL.revokeObjectURL(url);
    iframe.remove();
  }, 120000);
}

export function DocumentLinks({ order, size = "sm" }) {
  const docs = uniqueDocs(order.documents);
  if (!docs.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="order-documents">
      {docs.map((d) => (
        <span key={d.type} className="inline-flex items-center gap-1">
          <a
            href={`${API}/orders/${order.id}/documents/${d.type}`}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-1.5 rounded-full border border-[#6B1226]/25 text-[#6B1226] hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors ${
              size === "sm" ? "px-3 py-1.5 text-[0.65rem]" : "px-4 py-2 text-xs"
            } font-semibold tracking-wide`}
            data-testid={`doc-link-${d.type}`}
          >
            <FileText size={12} /> {d.label}
            {d.version > 1 ? ` v${d.version}` : ""}
          </a>
          <button
            onClick={() => printDocument(order.id, d.type).catch(() => toast.error("Impression impossible"))}
            className="p-1.5 rounded-full text-[#8C6D23] hover:bg-[#F5EEDC] transition-colors"
            title={`Imprimer ${d.label}`}
            data-testid={`doc-print-${d.type}`}
            aria-label={`Imprimer ${d.label}`}
          >
            <Printer size={13} />
          </button>
        </span>
      ))}
    </div>
  );
}

function PaymentPanel({ order, onDone }) {
  const [products, setProducts] = useState(false);
  const [payment, setPayment] = useState(false);
  const [method, setMethod] = useState(PAYMENT_METHODS[0]);
  const [busy, setBusy] = useState(false);
  const ready = products && payment;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/validate-payment`, {
        payment_method: method,
        products_checked: products,
        payment_checked: payment,
      });
      toast.success(`Facture acquittée générée pour ${order.number}`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-[#C5A059]/40 bg-[#F5EEDC]/60 p-4" data-testid="payment-validation-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-lg font-semibold text-[#1C1917]">Validation au restaurant</p>
        <p className="price text-xl text-[#6B1226]">
          <span className="text-xs font-normal text-[#57534E] font-sans">Montant à régler </span>
          {eur(order.total)}
        </p>
      </div>

      <select
        value={method}
        onChange={(e) => setMethod(e.target.value)}
        className="mt-3 w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#6B1226]/50"
        data-testid="payment-method-select"
      >
        {PAYMENT_METHODS.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>

      <label className="mt-3 flex items-start gap-2.5 text-sm text-[#1C1917] cursor-pointer" data-testid="payment-check-products-label">
        <input
          type="checkbox"
          checked={products}
          onChange={(e) => setProducts(e.target.checked)}
          className="accent-[#6B1226] w-4 h-4 mt-0.5"
          data-testid="payment-check-products"
        />
        Produits vérifiés et remis au client
      </label>
      <label className="mt-2 flex items-start gap-2.5 text-sm text-[#1C1917] cursor-pointer" data-testid="payment-check-done-label">
        <input
          type="checkbox"
          checked={payment}
          onChange={(e) => setPayment(e.target.checked)}
          className="accent-[#6B1226] w-4 h-4 mt-0.5"
          data-testid="payment-check-done"
        />
        Encaissement effectué et vérifié
      </label>

      <button
        onClick={submit}
        disabled={!ready || busy}
        className="mt-4 w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3 text-sm font-bold tracking-wide hover:bg-[#580E1D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="payment-validate-button"
      >
        {busy ? "Validation…" : ready ? "Valider et établir la facture acquittée" : "En attente de validation"}
      </button>
      {!ready && (
        <p className="mt-2 text-[0.65rem] text-[#8C6D23] italic">
          La commande ne peut pas être clôturée tant que les vérifications ne sont pas effectuées.
        </p>
      )}
    </div>
  );
}

function WineForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY_WINE);
  const [busy, setBusy] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock, 10) || 0 };
    try {
      if (form.id) {
        await api.put(`/admin/wines/${form.id}`, payload);
        toast.success("Vin mis à jour");
      } else {
        await api.post("/admin/wines", payload);
        toast.success("Vin ajouté à la cave");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="admin-wine-form-modal">
      <div className="absolute inset-0 bg-[#1C1917]/60 backdrop-blur-sm" onClick={onClose} />
      <motion.form
        onSubmit={submit}
        className="relative w-full sm:max-w-lg bg-[#FAF6F0] rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto no-scrollbar space-y-3"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        data-lenis-prevent
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-2xl font-semibold text-[#1C1917]">{form.id ? "Modifier le vin" : "Nouveau vin"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#6B1226]/10" data-testid="admin-wine-form-close" aria-label="Fermer">
            <X size={18} />
          </button>
        </div>

        {form.image ? (
          <div className="flex justify-center py-2">
            <img
              src={form.image}
              alt={form.name || "Bouteille"}
              {...pressZoom(() => setZoomSrc(form.image))}
              onClick={() => setZoomSrc(form.image)}
              className="h-40 w-auto object-contain no-frame cursor-zoom-in bottle-hover"
              data-testid="admin-wine-image-preview"
            />
          </div>
        ) : null}

        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du vin" className={inputCls} data-testid="admin-wine-name" />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="admin-wine-category">
            {Object.entries(CATEGORY_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
          <input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Millésime" className={inputCls} data-testid="admin-wine-year" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.producer} onChange={(e) => setForm({ ...form, producer: e.target.value })} placeholder="Maison / viticulteur" className={inputCls} data-testid="admin-wine-producer" />
          <input value={form.appellation} onChange={(e) => setForm({ ...form, appellation: e.target.value })} placeholder="Appellation" className={inputCls} data-testid="admin-wine-appellation" />
        </div>
        <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Région" className={inputCls} data-testid="admin-wine-region" />
        <div className="grid grid-cols-2 gap-3">
          <input required type="number" step="0.10" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Prix TTC (€)" className={inputCls} data-testid="admin-wine-price" />
          <input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock" className={inputCls} data-testid="admin-wine-stock" />
        </div>
        <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="URL de l'image (bouteille détourée)" className={inputCls} data-testid="admin-wine-image" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description : cépages, terroir, notes de dégustation" rows={3} className={`${inputCls} resize-none`} data-testid="admin-wine-description" />

        <p className="eyebrow pt-2">Fiche technique (facultatif)</p>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.volume} onChange={(e) => setForm({ ...form, volume: e.target.value })} placeholder="Contenance (ex. 75 cl)" className={inputCls} data-testid="admin-wine-volume" />
          <input value={form.alcohol_degree} onChange={(e) => setForm({ ...form, alcohol_degree: e.target.value })} placeholder="Degré (ex. 13,5%)" className={inputCls} data-testid="admin-wine-degree" />
        </div>
        <input value={form.cepages} onChange={(e) => setForm({ ...form, cepages: e.target.value })} placeholder="Cépages (ex. Grenache, Syrah)" className={inputCls} data-testid="admin-wine-cepages" />
        <div className="grid grid-cols-2 gap-3">
          <input value={form.garde} onChange={(e) => setForm({ ...form, garde: e.target.value })} placeholder="Garde (ex. 5-8 ans)" className={inputCls} data-testid="admin-wine-garde" />
          <input value={form.temperature_service} onChange={(e) => setForm({ ...form, temperature_service: e.target.value })} placeholder="Service (ex. 16-18°C)" className={inputCls} data-testid="admin-wine-temperature" />
        </div>
        <input value={form.allergenes} onChange={(e) => setForm({ ...form, allergenes: e.target.value })} placeholder="Allergènes (ex. Contient des sulfites)" className={inputCls} data-testid="admin-wine-allergenes" />

        <p className="eyebrow pt-2">Fournisseur (facultatif — visible admin uniquement)</p>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Nom du fournisseur" className={inputCls} data-testid="admin-wine-supplier-name" />
          <input value={form.supplier_contact} onChange={(e) => setForm({ ...form, supplier_contact: e.target.value })} placeholder="Contact (commercial)" className={inputCls} data-testid="admin-wine-supplier-contact" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input type="tel" value={form.supplier_phone} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} placeholder="Téléphone" className={inputCls} data-testid="admin-wine-supplier-phone" />
          <input type="email" value={form.supplier_email} onChange={(e) => setForm({ ...form, supplier_email: e.target.value })} placeholder="Email" className={inputCls} data-testid="admin-wine-supplier-email" />
        </div>
        <input value={form.supplier_website} onChange={(e) => setForm({ ...form, supplier_website: e.target.value })} placeholder="Site web" className={inputCls} data-testid="admin-wine-supplier-website" />
        <input value={form.supplier_address} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} placeholder="Adresse du fournisseur" className={inputCls} data-testid="admin-wine-supplier-address" />

        <label className="flex items-center gap-2.5 text-sm text-[#57534E] cursor-pointer">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#6B1226] w-4 h-4" data-testid="admin-wine-featured" />
          Mettre en avant sur l'accueil
        </label>
        <button type="submit" disabled={busy} className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60" data-testid="admin-wine-save-button">
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
        <ImageZoom src={zoomSrc} alt={form.name || "Bouteille"} onClose={() => setZoomSrc(null)} />
      </motion.form>
    </motion.div>
  );
}

function OrderItemsEditor({ order, wines, onDone }) {
  const [lines, setLines] = useState(order.items.map((it) => ({ wine_id: it.wine_id, name: it.name, price: it.price, qty: it.qty })));
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const totalQty = lines.reduce((s, l) => s + l.qty, 0);

  const changeQty = (id, delta) =>
    setLines((prev) => prev.map((l) => (l.wine_id === id ? { ...l, qty: l.qty + delta } : l)).filter((l) => l.qty > 0));

  const addLine = () => {
    const w = (wines || []).find((x) => x.id === pick);
    if (!w) return;
    setLines((prev) => {
      const found = prev.find((l) => l.wine_id === w.id);
      if (found) return prev.map((l) => (l.wine_id === w.id ? { ...l, qty: l.qty + 1 } : l));
      return [...prev, { wine_id: w.id, name: w.name, price: w.price, qty: 1 }];
    });
    setPick("");
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put(`/admin/orders/${order.id}/items`, { items: lines.map((l) => ({ wine_id: l.wine_id, qty: l.qty })) });
      toast.success(`Commande ${order.number} modifiée — documents régénérés`);
      onDone();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 bg-[#F4EFE6] rounded-2xl border border-[#6B1226]/15 p-4 space-y-3" data-testid="order-items-editor">
      <p className="eyebrow">Modifier la commande (avant paiement)</p>
      <ul className="space-y-2">
        {lines.map((l) => (
          <li key={l.wine_id} className="flex items-center justify-between gap-2 bg-white rounded-xl px-3 py-2 text-sm" data-testid="order-edit-line">
            <span className="flex-1 min-w-0 truncate">{l.name}</span>
            <span className="flex items-center border border-[#6B1226]/20 rounded-full shrink-0">
              <button onClick={() => changeQty(l.wine_id, -1)} className="p-1 px-2 text-[#6B1226]" data-testid="order-edit-minus" aria-label="Moins">
                <Minus size={12} />
              </button>
              <span className="w-7 text-center text-xs font-bold">{l.qty}</span>
              <button onClick={() => changeQty(l.wine_id, 1)} className="p-1 px-2 text-[#6B1226]" data-testid="order-edit-plus" aria-label="Plus">
                <Plus size={12} />
              </button>
            </span>
            <span className="price text-[#6B1226] text-sm w-16 text-right">{eur(l.price * l.qty)}</span>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} className={`${inputCls} flex-1`} data-testid="order-edit-wine-select">
          <option value="">Ajouter un produit…</option>
          {(wines || []).map((w) => (
            <option key={w.id} value={w.id}>{w.name} — {eur(w.price)}</option>
          ))}
        </select>
        <button type="button" onClick={addLine} disabled={!pick} className="shrink-0 rounded-full bg-[#6B1226] text-[#FAF6F0] px-4 py-2 text-xs font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-40" data-testid="order-edit-add-line">
          <Plus size={14} />
        </button>
      </div>
      <p className="text-xs text-[#57534E]">Nouveau total provisoire : <span className="price text-[#6B1226]">{eur(total)}</span> ({totalQty} bt, remise recalculée à l'enregistrement)</p>
      {error && <p className="text-xs text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-3 py-2.5" data-testid="order-edit-error">{error}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={busy || lines.length === 0} className="flex-1 rounded-full bg-[#6B1226] text-[#FAF6F0] py-2.5 text-xs font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-50" data-testid="order-edit-save">
          {busy ? "Enregistrement…" : "Enregistrer les modifications"}
        </button>
        <button onClick={onDone} className="rounded-full border border-[#6B1226]/25 text-[#6B1226] px-4 py-2.5 text-xs font-semibold hover:bg-[#6B1226]/5 transition-colors" data-testid="order-edit-cancel">
          Annuler
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState(null);
  const [wines, setWines] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const [editingOrder, setEditingOrder] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [newResCount, setNewResCount] = useState(0);

  const fetchOrders = () => api.get("/admin/orders").then((res) => setOrders(res.data)).catch(() => setOrders([]));
  const fetchWines = () => api.get("/wines").then((res) => setWines(res.data)).catch(() => setWines([]));

  useEffect(() => {
    if (user?.role === "admin") {
      fetchOrders();
      fetchWines();
      api
        .get("/admin/reservations")
        .then((res) => setNewResCount(res.data.filter((r) => r.status === "nouvelle").length))
        .catch(() => {});
    }
  }, [user, tab]);

  if (loading) {
    return (
      <div className="py-32 text-center" data-testid="admin-loading">
        <p className="font-display text-2xl text-[#6B1226] italic">Un instant…</p>
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="py-32 text-center px-4" data-testid="admin-forbidden">
        <p className="eyebrow mb-3">Espace réservé</p>
        <h1 className="font-display text-4xl font-semibold text-[#1C1917]">Le sommelier seulement</h1>
        <p className="text-sm text-[#57534E] mt-3">Connectez-vous avec un compte administrateur.</p>
        <Link to="/profil" className="inline-block mt-6 rounded-full bg-[#6B1226] text-[#FAF6F0] px-7 py-3 text-sm font-semibold hover:bg-[#580E1D] transition-colors" data-testid="admin-login-link">
          Se connecter
        </Link>
      </div>
    );
  }

  const setStatus = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status });
      toast.success(`Commande ${order.number} mise à jour`);
      fetchOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteWine = async (wine) => {
    if (!window.confirm(`Supprimer « ${wine.name} » de la cave ?`)) return;
    try {
      await api.delete(`/admin/wines/${wine.id}`);
      toast.success("Vin retiré de la cave");
      fetchWines();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Supprimer définitivement la commande ${order.number} ? Les documents associés seront effacés.`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Commande ${order.number} supprimée`);
      fetchOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-page">
      <section className="app-container px-4 sm:px-8 py-10 sm:py-14">
        <Reveal>
          <p className="eyebrow mb-2">Super Administration</p>
          <h1 className="font-display text-4xl font-semibold text-[#1C1917]">Le carnet du sommelier</h1>

          <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {[
              { id: "clients", label: "Clients" },
              { id: "orders", label: "Commandes" },
              { id: "reservations", label: "Tables" },
              { id: "wines", label: "La cave" },
              { id: "messages", label: "Messages" },
              { id: "notifications", label: "Notifications" },
              { id: "producers", label: "Fournisseurs" },
              { id: "qrcodes", label: "QR Codes" },
              { id: "users", label: "Équipe" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`rounded-full px-5 py-2.5 text-xs font-semibold tracking-wide transition-colors ${
                  tab === t.id ? "bg-[#6B1226] text-[#FAF6F0]" : "bg-white border border-[#6B1226]/15 text-[#57534E] hover:text-[#6B1226]"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                {t.label}
                {t.id === "reservations" && newResCount > 0 && (
                  <span
                    className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-[#C5A059] text-[#1C1917] text-[0.65rem] font-bold"
                    data-testid="admin-reservations-badge"
                  >
                    {newResCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Reveal>

        {tab === "reservations" && <AdminReservations />}

        {tab === "orders" && (
          <div className="mt-8" data-testid="admin-orders-panel">
            {orders === null ? (
              <p className="text-sm text-[#57534E] italic">Chargement…</p>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#6B1226]/10 p-10 text-center" data-testid="admin-orders-empty">
                <p className="font-display text-xl text-[#1C1917]">Aucune commande</p>
                <p className="text-sm text-[#57534E] mt-1.5">Les enlèvements apparaîtront ici.</p>
              </div>
            ) : (
              <ul className="space-y-4" data-testid="admin-orders-table">
                {orders.map((order) => (
                  <li key={order.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-5" data-testid="admin-order-card">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-eyebrow text-xs tracking-[0.25em] text-[#6B1226]">N° {order.number}</p>
                        <p className="text-sm font-semibold text-[#1C1917] mt-1">{order.customer_name} · {order.customer_phone}</p>
                        <p className="text-xs text-[#78716C] mt-0.5">
                          {order.pickup_time} · {new Date(order.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={order.status}
                          onChange={(e) => setStatus(order, e.target.value)}
                          className="bg-[#F4EFE6] border border-[#6B1226]/15 rounded-full px-4 py-2 text-xs font-semibold text-[#6B1226] outline-none cursor-pointer"
                          data-testid="admin-order-status-select"
                        >
                          {Object.entries(ORDER_STATUS).map(([id, s]) => (
                            <option key={id} value={id}>{s.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => deleteOrder(order)}
                          className="p-2 rounded-full text-stone-400 hover:text-[#6B1226] hover:bg-[#6B1226]/5 transition-colors"
                          title="Supprimer la commande"
                          data-testid="admin-order-delete"
                          aria-label="Supprimer la commande"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <ul className="mt-3 text-sm text-[#57534E] space-y-1">
                      {order.items.map((item, i) => (
                        <li key={i} className="flex justify-between gap-3">
                          <span>{item.qty} × {item.name}</span>
                          <span>{eur(item.line_total)}</span>
                        </li>
                      ))}
                    </ul>

                    {!order.paid && (
                      editingOrder === order.id ? (
                        <OrderItemsEditor
                          order={order}
                          wines={wines || []}
                          onDone={() => {
                            setEditingOrder(null);
                            fetchOrders();
                          }}
                        />
                      ) : (
                        <button
                          onClick={() => setEditingOrder(order.id)}
                          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B1226] border border-[#6B1226]/25 rounded-full px-4 py-2 hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
                          data-testid="admin-order-edit-button"
                        >
                          <Pencil size={12} /> Modifier les produits
                        </button>
                      )
                    )}

                    {order.discount_total > 0 && (
                      <p className="mt-2 text-xs text-[#8C6D23]">
                        Remise panachée (−{order.discount_rate.toFixed(2).replace(".", ",")} €/bt × {order.total_qty}) : −{eur(order.discount_total)}
                      </p>
                    )}

                    {order.note && (
                      <div className="mt-3 bg-[#F5EEDC] border border-[#C5A059]/50 rounded-xl px-4 py-3" data-testid="order-note">
                        <p className="font-eyebrow text-[0.55rem] tracking-[0.18em] uppercase font-bold text-[#8C6D23] mb-1 flex items-center gap-1.5">
                          <MessageSquare size={11} /> Message du client
                        </p>
                        <p className="text-sm text-[#44403C] italic leading-relaxed">« {order.note} »</p>
                      </div>
                    )}

                    <div className="mt-3 pt-3 border-t border-[#6B1226]/10 flex flex-wrap justify-between items-center gap-2 text-sm">
                      <span />
                      <span className="price text-[#6B1226]">{eur(order.total)}</span>
                    </div>

                    {order.paid ? (
                      <div className="mt-3 flex items-center gap-2 rounded-xl bg-[#3F6212]/10 border border-[#3F6212]/25 px-4 py-3" data-testid="order-paid-badge">
                        <BadgeCheck size={16} className="text-[#3F6212]" />
                        <p className="text-xs text-[#3F6212] font-semibold">
                          Acquittée · {order.payment_method} · {order.invoice_number}
                          {order.validated_by ? ` · validée par ${order.validated_by}` : ""}
                        </p>
                      </div>
                    ) : order.status !== "annulee" ? (
                      <PaymentPanel order={order} onDone={fetchOrders} />
                    ) : null}

                    <DocumentLinks order={order} />
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/admin/orders/${order.id}/regenerate-documents`);
                          toast.success(`Documents de ${order.number} régénérés (logo + filigrane)`);
                          fetchOrders();
                        } catch (err) {
                          toast.error(formatApiError(err));
                        }
                      }}
                      className="mt-2 inline-flex items-center gap-1.5 text-[0.65rem] font-semibold text-[#8C6D23] border border-[#C5A059]/50 rounded-full px-3 py-1.5 hover:bg-[#F5EEDC] transition-colors"
                      data-testid="admin-order-regenerate"
                    >
                      <RefreshCw size={11} /> Régénérer les documents (nouveau gabarit)
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "wines" && (
          <div className="mt-8" data-testid="admin-wines-panel">
            <button
              onClick={() => setEditing(EMPTY_WINE)}
              className="inline-flex items-center gap-2 rounded-full bg-[#6B1226] text-[#FAF6F0] px-6 py-3 text-sm font-semibold hover:bg-[#580E1D] transition-colors"
              data-testid="admin-wine-add-button"
            >
              <Plus size={16} /> Ajouter un vin
            </button>
            {wines === null ? (
              <p className="mt-6 text-sm text-[#57534E] italic">Chargement…</p>
            ) : (
              <ul className="mt-6 grid gap-3" data-testid="admin-wines-list">
                {wines.map((wine) => (
                  <li key={wine.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-4 flex items-center gap-4" data-testid="admin-wine-row">
                    <img
                      src={wine.image}
                      alt={wine.name}
                      {...pressZoom(() => setZoomSrc(wine.image))}
                      onClick={() => setZoomSrc(wine.image)}
                      className="h-14 w-auto object-contain no-frame shrink-0 cursor-zoom-in bottle-hover"
                      data-testid="admin-wine-image"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-semibold text-[#1C1917] truncate">{wine.name}</p>
                      <p className="text-xs text-[#78716C]">
                        {CATEGORY_LABELS[wine.category]} · {wine.appellation || wine.year || "—"} · Stock {wine.stock}
                        {wine.featured ? " · ★ À la une" : ""}
                      </p>
                    </div>
                    <span className="price text-[#6B1226] shrink-0">{eur(wine.price)}</span>
                    <button onClick={() => setEditing(wine)} className="p-2 rounded-full text-[#6B1226] hover:bg-[#6B1226]/10 transition-colors" data-testid="admin-wine-edit-button" aria-label="Modifier">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => deleteWine(wine)} className="p-2 rounded-full text-stone-400 hover:text-[#6B1226] hover:bg-[#6B1226]/10 transition-colors" data-testid="admin-wine-delete-button" aria-label="Supprimer">
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "clients" && <AdminClients />}
        {tab === "messages" && <AdminMessenger />}
        {tab === "notifications" && <AdminNotifications />}
        {tab === "producers" && <AdminProducers />}
        {tab === "qrcodes" && <AdminQrCodes />}
        {tab === "users" && <AdminUsers />}
      </section>
      <Footer />
      <ImageZoom src={zoomSrc} alt="Bouteille" onClose={() => setZoomSrc(null)} />
      <AnimatePresence>
        {editing !== undefined && <WineForm initial={editing.id ? editing : null} onClose={() => setEditing(undefined)} onSaved={fetchWines} />}
      </AnimatePresence>
    </div>
  );
}
